/* eslint-disable no-await-in-loop */

/* eslint-disable no-return-await */

/* eslint-disable class-methods-use-this */
import { HTTP_STATUSES } from "@miplatform/constants";
import { createUUID } from "@miplatform/utils";
import { axiosInstance, axiosInternal } from "../apiInstance/apiInstance";
import RequestDeduplicator from "./RequestDeduplicator";
import RequestQueueManager from "./RequestQueueManager";
import { DEFAULT_API_HANDLER_CONFIG } from "./constants";
import { reportCancel, reportError, reportSuccess } from "./util";

export class ApiHandler {
  constructor(config = {}) {
    this.config = { ...DEFAULT_API_HANDLER_CONFIG, ...config };
    this.deduplicator = new RequestDeduplicator(this.config);
    this.queueManager = new RequestQueueManager(this.config);
  }

  async apiRequest(request, toastMessageConfig = {}, options = {}) {
    const {
      bypassDeduplication = false,
      enableCaching = this.config.enableCaching,
      onDownloadProgress,
      onUploadProgress,
    } = options;

    const enhancedRequest = {
      ...request,
      requestId: request.requestId || createUUID(),
    };

    const createExecutor = () => {
      return async () => {
        const requestConfig = {
          ...enhancedRequest,
        };

        // Add progress callbacks if provided
        if (onDownloadProgress) {
          requestConfig.onDownloadProgress = onDownloadProgress;
        }
        if (onUploadProgress) {
          requestConfig.onUploadProgress = onUploadProgress;
        }

        return this.executeRequest(requestConfig, toastMessageConfig);
      };
    };

    // Deduplication logic to handle non-GET requests with allowDeduplication
    const method = (request.method || "GET").toLowerCase();
    const shouldDeduplicate =
      method === "get" || request.allowDeduplication === true;

    if (shouldDeduplicate && !onDownloadProgress && !onUploadProgress) {
      return this.deduplicator.deduplicateRequest(
        enhancedRequest,
        () => {
          return this.queueManager.processRequest(enhancedRequest, async () => {
            return this.executeRequest(enhancedRequest, toastMessageConfig);
          });
        },
        { enableCaching, bypassDeduplication }
      );
    }

    // For other requests, use queue manager directly with proper executor
    return this.queueManager.processRequest(enhancedRequest, createExecutor());
  }

  async executeRequest(request, toastMessageConfig = {}) {
    try {
      const result = await axiosInstance({
        paramsSerializer: {
          indexes: null,
        },
        ...request,
      });

      if (result?.status < HTTP_STATUSES.BAD_REQUEST) {
        return reportSuccess(result, request.url, toastMessageConfig.success);
      }

      throw result;
    } catch (error) {
      if (axiosInternal.isCancel(error)) {
        reportCancel(error, request.url);

        return {
          canceled: true,
          request,
          message: "This request was cancelled",
          timestamp: new Date().toISOString(),
        };
      }

      return reportError(error, request, toastMessageConfig.failure);
    }
  }

  invalidateCache(pattern) {
    this.deduplicator.invalidateCache(pattern);
  }

  getStats() {
    return {
      pendingRequests: this.deduplicator.pendingRequests.size,
      cachedResponses: this.deduplicator.requestCache.size,
      queuedRequests: this.queueManager.requestQueue.length,
      activeRequests: this.queueManager.currentRequests.size,
    };
  }

  cleanup() {
    this.deduplicator.cleanup();
    this.queueManager.cleanup();
  }
}
